import React, { useMemo, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Button,
  Table,
  Badge,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  BoxArrowRight,
  Bell,
  PersonCircle,
  Book,
  Bullseye,
  ChevronRight,
  Calendar3,
} from "react-bootstrap-icons";
import LOGO_URL from "../img/tmis_logo_orig.png";
import axios from "axios";
import LicenseRenewalModal from "../components/LicenseRenewalModal.js";

const BRAND_BLUE = "#183B72";
const SURFACE = "#f2f4f7";
const API_BASE = process.env.REACT_APP_API || "";

const ENDPOINT_ANALYZE_BADGE = `${API_BASE}/analyzeFile`;
const ENDPOINT_UPDATE_LICENCE = `${API_BASE}/userUpdateLicence`;

const getSingaporeNow = () => new Date(Date.now() + 8 * 60 * 60 * 1000);

const enableDate = new Date("2026-03-01T00:00:00+08:00");
const isEnabled = getSingaporeNow() >= enableDate;

function getAuthToken() {
  const keys = ["token", "authToken", "auth_token"];
  for (const k of keys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v) return v;
  }
  return "";
}

function parseDateFlexible(input) {
  if (!input && input !== 0) return null;

  if (input instanceof Date) {
    const dt = new Date(input.getFullYear(), input.getMonth(), input.getDate());
    return isNaN(+dt) ? null : dt;
  }

  if (typeof input === "number") {
    const d = new Date(input);
    if (isNaN(+d)) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  if (typeof input === "string") {
    const v = input.trim();
    if (!v) return null;

    // yyyy-mm-dd or yyyy/mm/dd
    let m = v.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (m) {
      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10) - 1;
      const day = parseInt(m[3], 10);
      const dt = new Date(y, mo, day, 0, 0, 0, 0);
      return isNaN(+dt) ? null : dt;
    }

    // dd-mm-yyyy or dd/mm/yyyy
    m = v.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (m) {
      const day = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10) - 1;
      const y = parseInt(m[3], 10);
      const dt = new Date(y, mo, day, 0, 0, 0, 0);
      return isNaN(+dt) ? null : dt;
    }

    // dd Mon yyyy
    m = v.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
    if (m) {
      const day = parseInt(m[1], 10);
      const monText = m[2].toLowerCase();
      const y = parseInt(m[3], 10);

      const monthMap = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11,
      };

      const mo = monthMap[monText];
      if (mo != null) {
        const dt = new Date(y, mo, day, 0, 0, 0, 0);
        return isNaN(+dt) ? null : dt;
      }
    }

    const d2 = new Date(v);
    if (!isNaN(+d2)) {
      return new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
    }
  }

  return null;
}

function normalizeToYMD(input) {
  if (!input && input !== 0) return "";

  if (typeof input === "string") {
    const v = input.trim();
    if (!v) return "";

    const ymd = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return v;

    const dmyText = v.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
    if (dmyText) {
      const dd = String(parseInt(dmyText[1], 10)).padStart(2, "0");
      const monText = dmyText[2].toLowerCase();
      const yyyy = dmyText[3];

      const monthMap = {
        jan: "01",
        feb: "02",
        mar: "03",
        apr: "04",
        may: "05",
        jun: "06",
        jul: "07",
        aug: "08",
        sep: "09",
        oct: "10",
        nov: "11",
        dec: "12",
      };

      return `${yyyy}-${monthMap[monText] || "01"}-${dd}`;
    }

    const dmy = v.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmy) {
      const dd = String(parseInt(dmy[1], 10)).padStart(2, "0");
      const mm = String(parseInt(dmy[2], 10)).padStart(2, "0");
      const yyyy = dmy[3];
      return `${yyyy}-${mm}-${dd}`;
    }
  }

  const d = parseDateFlexible(input) || new Date(input);
  if (isNaN(d)) return "";

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function daysFromToday(target) {
  if (!(target instanceof Date) || isNaN(+target)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t = new Date(target);
  t.setHours(0, 0, 0, 0);
  const diffMs = t.getTime() - today.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function readUserFromStorage() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
      displayName:
        localStorage.getItem("display_name") ||
        user.display_name ||
        localStorage.getItem("username") ||
        "User Acc",
      tgExpiryRaw:
        user.tg_expiry_date ??
        user.tgExpiryDate ??
        localStorage.getItem("tg_expiry_date") ??
        null,
      userId: user.user_id ?? user.id ?? "",
      badgeNo: user.badge_no || "",
      fullName:
        user.display_name ||
        `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
        localStorage.getItem("display_name") ||
        "User Acc",
      username: user.username || "",
      email: user.email || user.email_address || "",
      nonLicenceExpiryDate: user.nonLicenceExpiryDate || "",
    };
  } catch {
    return {
      displayName:
        localStorage.getItem("display_name") ||
        localStorage.getItem("username") ||
        "User Acc",
      tgExpiryRaw: localStorage.getItem("tg_expiry_date") ?? null,
      userId: "",
      badgeNo: "",
      fullName:
        localStorage.getItem("display_name") ||
        localStorage.getItem("username") ||
        "User Acc",
      username: localStorage.getItem("username") || "",
      email: localStorage.getItem("email") || "",
      nonLicenceExpiryDate: "",
    };
  }
}

function formatToDDMMYYYY(dateInput) {
  if (!dateInput) return "";
  const d = parseDateFlexible(dateInput) || new Date(dateInput);
  if (isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function clampPercent(v) {
  const n = Number(v);
  if (!isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function CircularProgress({
  percent = 0,
  size = 92,
  stroke = 10,
  trackColor = "#E9EDF3",
  progressColor = "#2e9b67",
  textColor = "#111",
}) {
  const p = clampPercent(percent);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (p / 100) * c;

  return (
    <div style={{ width: size, height: size, position: "relative" }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={trackColor}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={progressColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>

      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 900,
          color: textColor,
          fontSize: 18,
        }}
      >
        {Math.round(p)}%
      </div>
    </div>
  );
}

function ProgressBlock({
  title,
  rightLabel = "SUBMISSION",
  icon,
  circleColor,
  percent,
  completed,
  total,
  remaining,
  byDateLabel,
  onCreate,
  disabled = false,
  disabledMessage = "",
}) {
  const safeByDate = byDateLabel?.trim() ? byDateLabel.trim() : "—";

  return (
    <Card
      className="h-100"
      style={{
        borderRadius: 18,
        border: "1px solid rgba(0,0,0,0.06)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
      }}
    >
      <Card.Body style={{ padding: 18 }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: 999,
                background: circleColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </div>
            <div style={{ fontWeight: 900, letterSpacing: 0.4 }}>{title}</div>
          </div>
          <div style={{ fontSize: 12, color: "#1F2937" }}>{rightLabel}</div>
        </div>

        <div className="d-flex align-items-center mt-3" style={{ gap: 16 }}>
          <CircularProgress percent={percent} progressColor={circleColor} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#2e2d2d" }}>
              {completed} / {total} Completed
            </div>

            <div style={{ fontSize: 14, color: "#1F2937", marginTop: 8 }}>
              <b style={{ color: "#2e2d2d" }}>{remaining}</b> Remaining by{" "}
              <b style={{ color: "#2e2d2d" }}>{safeByDate}</b>
            </div>

            <Button
              size="sm"
              onClick={onCreate}
              disabled={disabled}
              className="d-inline-flex align-items-center gap-1"
              style={{
                marginTop: 10,
                borderRadius: 999,
                padding: "8px 18px",
                fontWeight: 600,
                fontSize: 13,
                background: disabled
                  ? "#c7cbd6"
                  : "linear-gradient(135deg, #2563eb, #4f46e5)",
                border: "none",
                boxShadow: disabled
                  ? "none"
                  : "0 6px 14px rgba(37, 99, 235, 0.25)",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.7 : 1,
                transition: "all .25s ease",
              }}
              onMouseEnter={(e) => {
                if (disabled) return;
                e.currentTarget.style.boxShadow =
                  "0 10px 22px rgba(37, 99, 235, 0.35)";
              }}
              onMouseLeave={(e) => {
                if (disabled) return;
                e.currentTarget.style.boxShadow =
                  "0 6px 14px rgba(37, 99, 235, 0.25)";
              }}
            >
              Create / Check History
              <ChevronRight size={16} />
            </Button>
            {disabled && disabledMessage ? (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: "#92400e",
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: 10,
                  padding: "8px 10px",
                  fontWeight: 600,
                  lineHeight: 1.45,
                }}
              >
                {disabledMessage}
              </div>
            ) : null}
          </div>
        </div>

        <div
          className="mt-3"
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 12,
            color: "#1F2937",
          }}
        >
          <span>Approved: {completed}</span>
          <span>Total: {total}</span>
        </div>
      </Card.Body>
    </Card>
  );
}

function getTransitionRows(rawPlan) {
  if (!rawPlan) return [];

  if (Array.isArray(rawPlan)) return rawPlan;
  if (Array.isArray(rawPlan.cycles)) return rawPlan.cycles;
  if (Array.isArray(rawPlan.transitionPlan)) return rawPlan.transitionPlan;
  if (Array.isArray(rawPlan.plan)) return rawPlan.plan;

  return [];
}

export default function UserHome() {
  const navigate = useNavigate();
  const authToken = useMemo(getAuthToken, []);
  const userInfo = useMemo(readUserFromStorage, []);
  const {
    displayName,
    tgExpiryRaw,
    userId,
    badgeNo,
    fullName,
    username,
    email,
    nonLicenceExpiryDate,
  } = userInfo;

  const [showRenewal, setShowRenewal] = useState(false);
  const [renewalPromptShown, setRenewalPromptShown] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const goLearn = () => navigate("/learn");
  const goDo = () => navigate("/do");

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  //survey
  const [surveyList, setSurveyList] = useState([]);
  const [surveyLoading, setSurveyLoading] = useState(true);

  const [objProgress, setObjProgress] = useState({
    learnApproved: 0,
    doApproved: 0,
    learnReq: 0,
    doReq: 0,
    learnPercent: 0,
    doPercent: 0,
    eligible: true,
    tgExpiryDate: "",
    tgExpiryDateFmt: "",
    cycleStart: "",
    cycleEnd: "",
    cycleStartFmt: "",
    cycleEndFmt: "",
    daysRemaining: null,
    transitionPlan: [],
    nonLicenceExpiryDate: "",
    transitionPlanRaw: null,
  });

  const profileForRenewal = useMemo(() => {
    const rawExpirySource = objProgress?.tgExpiryDate || tgExpiryRaw || "";
    const rawNonLicenceExpirySource =
      objProgress?.nonLicenceExpiryDate || nonLicenceExpiryDate || "";

    const normalizedExpiry = normalizeToYMD(rawExpirySource);
    const normalizedNonLicenceExpiry = normalizeToYMD(
      rawNonLicenceExpirySource,
    );

    return {
      userId,
      badgeNo,
      fullName: fullName || displayName,
      username: username || email || "",
      tgExpiryDate: normalizedExpiry,
      nonLicenceExpiryDate: normalizedNonLicenceExpiry,
    };
  }, [
    userId,
    badgeNo,
    fullName,
    displayName,
    username,
    email,
    tgExpiryRaw,
    nonLicenceExpiryDate,
    objProgress?.tgExpiryDate,
    objProgress?.nonLicenceExpiryDate,
  ]);

  const getUserDetails = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const token =
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        localStorage.getItem("token") ||
        "";

      const { data } = await axios.get(`${API_BASE}/getLoggedInDetails`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      const obj = data?.data || {};

      const learnApproved = Number(
        obj.noOfLearnApproved ?? obj.learnApproved ?? 0,
      );
      const doApproved = Number(obj.noOfDoApproved ?? obj.doApproved ?? 0);

      const learnReq = Number(obj.noOfLearnReq ?? obj.learnReq ?? 0);
      const doReq = Number(obj.noOfDoReq ?? obj.doReq ?? 0);

      const learnPercent = learnReq > 0 ? (learnApproved / learnReq) * 100 : 0;
      const doPercent = doReq > 0 ? (doApproved / doReq) * 100 : 0;

      // backend shared:
      // tgExpiryDate = formatted date
      // tg_expiry_date = raw date
      const tgExpiryDateRaw =
        obj.tg_expiry_date || obj.tgExpiryDate || obj.expiryDate || "";

      // backend shared:
      // cycleStart: transitionPlan.submissionStarts
      // cycleEnd: transitionPlan.submissionEnds
      // but keep many fallbacks just in case
      // const cycleStartRaw =
      //   obj.submissionStarts ||
      //   obj.cycleStart ||
      //   obj.cycle_start ||
      //   obj.startDate ||
      //   "";

      // const cycleEndRaw =
      //   obj.submissionEnds ||
      //   obj.cycleEnd ||
      //   obj.cycle_end ||
      //   obj.endDate ||
      //   "";

      const nonLicenceExpiryDateRaw =
        obj.nonLicenceExpiryDate || obj.non_licence_expiry_date || "";

      const transitionPlanRaw =
        obj.transitionPlan || obj.transition_plan || obj.plan || [];

      const transitionRows = getTransitionRows(transitionPlanRaw);

      const firstActiveCycle =
        transitionRows.find((x) => Number(x.seq) === 1) ||
        transitionRows[0] ||
        {};

      const cycleStartRaw =
        obj.submissionStarts ||
        obj.cycleStart ||
        obj.cycle_start ||
        obj.startDate ||
        firstActiveCycle.cycle_start ||
        firstActiveCycle.cycleStart ||
        firstActiveCycle.submission_start ||
        firstActiveCycle.start ||
        firstActiveCycle.startDate ||
        firstActiveCycle.submissionStarts ||
        "";

      const cycleEndRaw =
        obj.submissionEnds ||
        obj.cycleEnd ||
        obj.cycle_end ||
        obj.endDate ||
        firstActiveCycle.cycle_end ||
        firstActiveCycle.cycleEnd ||
        firstActiveCycle.submission_end ||
        firstActiveCycle.end ||
        firstActiveCycle.endDate ||
        firstActiveCycle.submissionEnds ||
        "";

      // const cycleStartMessage = !hasCycleStarted
      //   ? `You can submit from ${
      //       objProgress.cycleStartFmt ||
      //       formatToDDMMYYYY(objProgress.cycleStart)
      //     }. Currently you cannot submit because this cycle has not started yet.`
      //   : "";

      const resolvedDaysRemaining =
        obj.daysRemaining != null && isFinite(Number(obj.daysRemaining))
          ? Number(obj.daysRemaining)
          : (() => {
              const exp = parseDateFlexible(tgExpiryDateRaw);
              return exp ? daysFromToday(exp) : null;
            })();

      setObjProgress({
        ...obj,
        learnApproved,
        doApproved,
        learnReq,
        doReq,
        learnPercent: clampPercent(learnPercent),
        doPercent: clampPercent(doPercent),
        eligible: obj.eligible !== false,
        tgExpiryDate: tgExpiryDateRaw,
        tgExpiryDateFmt: formatToDDMMYYYY(tgExpiryDateRaw),
        cycleStart: cycleStartRaw,
        cycleEnd: cycleEndRaw,
        cycleStartFmt: formatToDDMMYYYY(cycleStartRaw),
        cycleEndFmt: formatToDDMMYYYY(cycleEndRaw),
        nonLicenceExpiryDate: nonLicenceExpiryDateRaw,
        daysRemaining: resolvedDaysRemaining,
        transitionPlan: transitionRows,
        transitionPlanRaw,
      });
    } catch (e) {
      console.error("Failed to get user details:", e);
      setErrorMsg("Failed to load user details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserDetails();
    getSurveyList();
  }, []);

  // survey to display
  const getSurveyList = async () => {
    try {
      setSurveyLoading(true);

      const token =
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        localStorage.getItem("token") ||
        "";

      const { data } = await axios.get(`${API_BASE}/getSurvey`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      setSurveyList(
        (data?.data || []).filter((s) => Number(s.is_active) === 1),
      );
    } catch (err) {
      console.error("Failed to load surveys", err);
    } finally {
      setSurveyLoading(false);
    }
  };

  const licenseDaysRemainingToExpiry = useMemo(() => {
    if (
      objProgress.daysRemaining != null &&
      isFinite(Number(objProgress.daysRemaining))
    ) {
      return Number(objProgress.daysRemaining);
    }

    const dt = parseDateFlexible(objProgress.tgExpiryDate || tgExpiryRaw);
    return dt ? daysFromToday(dt) : null;
  }, [objProgress.daysRemaining, objProgress.tgExpiryDate, tgExpiryRaw]);

  useEffect(() => {
    if (loading) return;
    if (renewalPromptShown) return;

    if (
      licenseDaysRemainingToExpiry != null &&
      Number(licenseDaysRemainingToExpiry) <= 0
    ) {
      setShowRenewal(true);
      setRenewalPromptShown(true);
    }
  }, [loading, licenseDaysRemainingToExpiry, renewalPromptShown]);

  const daysLabel =
    licenseDaysRemainingToExpiry == null
      ? "—"
      : licenseDaysRemainingToExpiry <= 0
        ? "EXPIRED"
        : `${licenseDaysRemainingToExpiry} DAYS REMAINING`;

  const isLicenseExpired =
    licenseDaysRemainingToExpiry != null &&
    Number(licenseDaysRemainingToExpiry) <= 0;

  const learnRemaining = Math.max(
    0,
    Number(objProgress.learnReq || 0) - Number(objProgress.learnApproved || 0),
  );
  const doRemaining = Math.max(
    0,
    Number(objProgress.doReq || 0) - Number(objProgress.doApproved || 0),
  );

  const cycleEndLabelForBlocks =
    objProgress.cycleEndFmt ||
    (objProgress.cycleEnd ? formatToDDMMYYYY(objProgress.cycleEnd) : "") ||
    "—";

  const cycleDaysRemaining = useMemo(() => {
    const dt = parseDateFlexible(objProgress.cycleEnd);
    if (!dt) return null;
    return daysFromToday(dt);
  }, [objProgress.cycleEnd]);

  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);

  const cycleStartDate = parseDateFlexible(objProgress.cycleStart);

  const hasCycleStarted =
    cycleStartDate instanceof Date &&
    !isNaN(+cycleStartDate) &&
    cycleStartDate <= todayDate;

  const cycleStartMessage =
    !hasCycleStarted && (objProgress.cycleStartFmt || objProgress.cycleStart)
      ? `Submissions will open on ${
          objProgress.cycleStartFmt || formatToDDMMYYYY(objProgress.cycleStart)
        } date, when your new licence year starts. Kindly note that the LEARN + DO activities submitted must fall within your new licence year.`
      : "";

  const cycleVariant =
    cycleDaysRemaining == null
      ? "info"
      : cycleDaysRemaining <= 0
        ? "danger"
        : cycleDaysRemaining <= 30
          ? "warning"
          : "info";

  const transitionRows = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const currentPlan = (objProgress.transitionPlan || []).find(
      (x) => String(x.status || "").toLowerCase() === "ongoing",
    );

    const currentStart = parseDateFlexible(
      currentPlan?.cycle_start ||
        currentPlan?.submission_start ||
        objProgress.cycle_start ||
        objProgress.cycleStart ||
        "",
    );
    const currentEnd = parseDateFlexible(
      currentPlan?.cycle_end ||
        currentPlan?.submission_end ||
        objProgress.cycle_end ||
        objProgress.cycleEnd ||
        "",
    );

    return (objProgress.transitionPlan || []).map((item, index) => {
      const start =
        item.submission_start ||
        item.cycle_start ||
        item.cycleStart ||
        item.start ||
        item.startDate ||
        item.submissionStarts ||
        "";

      const end =
        item.submission_end ||
        item.cycle_end ||
        item.cycleEnd ||
        item.end ||
        item.endDate ||
        item.submissionEnds ||
        "";

      const learnReq = Number(
        item.learnReq ??
          item.no_learn ??
          item.learn ??
          item.noOfLearnReq ??
          item.learn_required ??
          0,
      );

      const doReq = Number(
        item.doReq ??
          item.no_do ??
          item.do ??
          item.noOfDoReq ??
          item.do_required ??
          0,
      );

      const startDate = parseDateFlexible(start);
      const endDate = parseDateFlexible(end);

      const isCurrent =
        (startDate &&
          endDate &&
          currentStart &&
          currentEnd &&
          startDate.getTime() === currentStart.getTime() &&
          endDate.getTime() === currentEnd.getTime()) ||
        String(item.status || "").toLowerCase() === "ongoing";

      let statusText = item.status || "Upcoming";
      let statusVariant = "secondary";

      statusText = item.status;
      if (String(item.status || "").toLowerCase() === "ongoing") {
        // statusText = "Ongoing";
        statusVariant = "primary";
      } else if (String(item.status || "").toLowerCase() === "completed") {
        // statusText = "Completed";
        statusVariant = "dark";
      } else if (String(item.status || "").toLowerCase() === "closed") {
        statusText = "Completed";
        statusVariant = "dark";
      } else {
        // statusText = "Upcoming";
        statusVariant = "secondary";
      }

      return {
        id: item.id,
        seq: item.seq ?? index + 1,
        no: item.seq ?? index + 1,
        badgeNo: item.badge_no || "",
        tgExpiryDate: item.tg_expiry_date || "",
        start,
        end,
        startFmt: formatToDDMMYYYY(start),
        endFmt: formatToDDMMYYYY(end),
        learnReq,
        doReq,
        isSkipCycle: Number(item.isSkipCycle || 0),
        isCurrent,
        statusText,
        statusVariant,
        rawStatus: item.status || "",
        createdDate: item.createdDate || item.created_date || "",
      };
    });
  }, [
    objProgress.transitionPlan,
    objProgress.cycle_start,
    objProgress.cycle_end,
    objProgress.cycleStart,
    objProgress.cycleEnd,
  ]);

  const handleRenewalSubmitted = async () => {
    await getUserDetails();
  };

  const handleRenewalClose = async () => {
    setShowRenewal(false);
    await getUserDetails();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: SURFACE,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header
        style={{
          background: "#fff",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Container>
          <div
            className="d-flex align-items-center justify-content-between py-2"
            style={{ gap: 16 }}
          >
            <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
            <div className="d-flex align-items-center" style={{ gap: 25 }}>
              <Bell size={20} color="#1A3584" />
              <BoxArrowRight
                size={20}
                color="#1A3584"
                style={{ cursor: "pointer" }}
                onClick={handleLogout}
                role="button"
                aria-label="Logout"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleLogout();
                }}
              />
              <span
                style={{ cursor: "pointer", fontWeight: "bold" }}
                className="custom-font"
                onClick={() => navigate("/profile")}
              >
                {displayName}
              </span>
              <PersonCircle
                size={23}
                color="#1A3584"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/profile")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") navigate("/profile");
                }}
              />
            </div>
          </div>
        </Container>
      </header>

      <section
        style={{
          background: BRAND_BLUE,
          color: "#fff",
          padding: "30px 0 40px",
        }}
      >
        <Container className="custom-font">
          <h2 style={{ fontWeight: 800, letterSpacing: 0.6, margin: 0 }}>
            Welcome Back,
          </h2>
          <div style={{ fontSize: 20, opacity: 0.95 }} className="mt-3 mb-3">
            {displayName}
          </div>
        </Container>
      </section>

      <section style={{ marginTop: 20, paddingBottom: 28 }}>
        <Container className="custom-font">
          {errorMsg ? (
            <Alert variant="danger" style={{ borderRadius: 14 }}>
              {errorMsg}
            </Alert>
          ) : null}

          <Card className="shadow-sm rounded-4 overflow-hidden">
            <div className="p-4 text-white" style={{ background: "#183b72" }}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <h5 className="mb-1 fw-bold">LICENSE RENEWAL STATUS</h5>
                  <small>
                    Expires:{" "}
                    {objProgress.tgExpiryDateFmt ||
                      objProgress.tgExpiryDate ||
                      "-"}
                  </small>
                </div>

                <div
                  className="bg-white text-black py-1 px-3 rounded-pill"
                  style={{ fontWeight: 800 }}
                >
                  {daysLabel}
                </div>
              </div>
            </div>

            <Card.Body style={{ padding: 20 }}>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <ProgressBlock
                      title="LEARN"
                      icon={<Book size={22} color="#fff" />}
                      circleColor="#FFC107"
                      percent={objProgress.learnPercent}
                      completed={objProgress.learnApproved}
                      total={objProgress.learnReq}
                      remaining={learnRemaining}
                      byDateLabel={cycleEndLabelForBlocks}
                      onCreate={goLearn}
                      disabled={
                        !isEnabled || isLicenseExpired || !hasCycleStarted
                      }
                      disabledMessage={
                        !hasCycleStarted ? cycleStartMessage : ""
                      }
                    />
                  </Col>

                  <Col xs={12} md={6}>
                    <ProgressBlock
                      title="DO"
                      icon={<Bullseye size={22} color="#fff" />}
                      circleColor="#FF6A57"
                      percent={objProgress.doPercent}
                      completed={objProgress.doApproved}
                      total={objProgress.doReq}
                      remaining={doRemaining}
                      byDateLabel={cycleEndLabelForBlocks}
                      onCreate={goDo}
                      disabled={
                        !isEnabled || isLicenseExpired || !hasCycleStarted
                      }
                      disabledMessage={
                        !hasCycleStarted ? cycleStartMessage : ""
                      }
                    />
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>

          {!isEnabled && (
            <Alert
              variant="info"
              className="mt-4 text-center"
              style={{
                borderRadius: 14,
                fontSize: 18,
                background: "#eef4ff",
                color: "#1e40af",
                border: "1px solid #c7d2fe",
              }}
            >
              LEARN + DO will commence on 1st March 2026.
            </Alert>
          )}

          {objProgress.cycleEnd && !loading && (
            <Alert
              className="mt-3 mb-0"
              style={{ borderRadius: 14, fontSize: 18 }}
              variant={cycleVariant}
            >
              {cycleDaysRemaining != null && cycleDaysRemaining <= 0 ? (
                <div className="fw-bold">
                  License year ended. Pending items — LEARN: {learnRemaining},
                  DO: {doRemaining}.
                </div>
              ) : (
                <>
                  <div>
                    Your license year will end on{" "}
                    {objProgress.cycleEndFmt
                      ? objProgress.cycleEndFmt
                      : formatToDDMMYYYY(objProgress.cycleEnd)}{" "}
                    {cycleDaysRemaining != null ? (
                      <>
                        ({cycleDaysRemaining} day
                        {cycleDaysRemaining === 1 ? "" : "s"} left).{" "}
                      </>
                    ) : (
                      <>. </>
                    )}
                    You are required to complete the following before this date.
                  </div>

                  <div className="mt-1" style={{ fontSize: 22 }}>
                    <span>
                      <b>{learnRemaining}</b> LEARN and
                    </span>
                    <span>
                      <b> {doRemaining}</b> DO
                    </span>
                  </div>
                </>
              )}
            </Alert>
          )}

          {/* Survey Button Section */}
          {!surveyLoading &&
            surveyList.some((s) => Number(s.is_active) === 1) && (
              <Card
                className="mt-4 shadow-sm rounded-4 overflow-hidden"
                style={{
                  border: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <div
                  className="p-4 text-white"
                  style={{
                    background: "linear-gradient(135deg, #f46b45, #eea849)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div>
                      <h5 className="mb-1 fw-bold">FEEDBACK / SURVEY</h5>
                      <div style={{ fontSize: 13, opacity: 0.9 }}>
                        Please complete the survey assigned to you.
                      </div>
                    </div>

                    {surveyLoading ? (
                      <button className="animated-button disabled">
                        <span className="text">Loading...</span>
                      </button>
                    ) : surveyList.some((s) => Number(s.is_active) === 1) ? (
                      <button
                        className="animated-button"
                        onClick={() =>
                          window.open(
                            surveyList.find((s) => Number(s.is_active) === 1)
                              ?.url,
                            "_blank",
                          )
                        }
                      >
                        <svg className="arr-2" viewBox="0 0 24 24">
                          <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
                        </svg>

                        <span className="text">Take Survey</span>

                        <span className="circle"></span>

                        <svg className="arr-1" viewBox="0 0 24 24">
                          <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                </div>
              </Card>
            )}

          <Card
            className="mt-4 shadow-sm rounded-4 overflow-hidden"
            style={{
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <div
              className="p-4 text-white"
              style={{
                background: "linear-gradient(135deg, #183b72, #24519a)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 999,
                      background: "rgba(255,255,255,0.16)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Calendar3 size={22} color="#fff" />
                  </div>
                  <div>
                    <h5 className="mb-1 fw-bold">TRANSITION PLAN</h5>
                    <div style={{ fontSize: 13, opacity: 0.9 }}>
                      Display how many LEARN and DO activities are required for
                      each cycle.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Card.Body style={{ padding: 20 }}>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : transitionRows.length === 0 ? (
                <Alert
                  variant="light"
                  className="mb-0"
                  style={{
                    borderRadius: 14,
                    border: "1px solid #e5e7eb",
                    color: "#1F2937",
                  }}
                >
                  No transition plan found.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table
                    hover
                    className="align-middle mb-0"
                    style={{
                      minWidth: 760,
                      overflow: "hidden",
                      borderRadius: 14,
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#f7f9fc" }}>
                        <th style={{ whiteSpace: "nowrap" }}>No</th>
                        <th style={{ whiteSpace: "nowrap" }}>Licence Year End Date</th>
                        <th style={{ whiteSpace: "nowrap" }}>LEARN Required</th>
                        <th style={{ whiteSpace: "nowrap" }}>DO Required</th>
                        <th style={{ whiteSpace: "nowrap" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transitionRows.map((row) => (
                        <tr
                          key={`${row.no}-${row.start}-${row.end}`}
                          style={{
                            background: row.isCurrent ? "#eef4ff" : "#fff",
                          }}
                        >
                          <td style={{ fontWeight: 700 }}>{row.no}</td>
                          <td>{row.endFmt || "-"}</td>
                          <td>
                            <Badge
                              pill
                              bg=""
                              style={{
                                background: "#fff3cd",
                                color: "#7a5a00",
                                fontWeight: 700,
                                padding: "8px 12px",
                              }}
                            >
                              {row.learnReq} Learn
                            </Badge>
                          </td>
                          <td>
                            <Badge
                              pill
                              bg=""
                              style={{
                                background: "#ffe1dc",
                                color: "#a52813",
                                fontWeight: 700,
                                padding: "8px 12px",
                              }}
                            >
                              {row.doReq} Do
                            </Badge>
                          </td>
                          <td>
                            <Badge
                              pill
                              bg={row.statusVariant}
                              style={{
                                padding: "8px 12px",
                                fontWeight: 700,
                                fontSize: 14,
                              }}
                            >
                              {row.statusText}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </section>

      <LicenseRenewalModal
        show={showRenewal}
        onHide={handleRenewalClose}
        profile={profileForRenewal}
        authToken={authToken}
        analyzeEndpoint={ENDPOINT_ANALYZE_BADGE}
        submitEndpoint={ENDPOINT_UPDATE_LICENCE}
        onSubmitted={handleRenewalSubmitted}
      />
    </div>
  );
}
